import { createContext, useContext, useEffect, useState } from "react";

type Mode = "light" | "dark";
export type Accent = "indigo" | "forest" | "rose" | "amber";

const ACCENTS: Accent[] = ["indigo", "forest", "rose", "amber"];

interface ThemeContextValue {
  /** Light/dark mode. */
  theme: Mode;
  toggle: () => void;
  /** Per-business accent template. */
  accent: Accent;
  setAccent: (a: Accent) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getInitialMode(): Mode {
  try {
    const stored = localStorage.getItem("theme");
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // localStorage unavailable
  }
  if (window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}

function getInitialAccent(): Accent {
  try {
    const stored = localStorage.getItem("accent");
    if (stored && (ACCENTS as string[]).includes(stored)) return stored as Accent;
  } catch {
    // localStorage unavailable
  }
  return "indigo";
}

function applyMode(mode: Mode) {
  document.documentElement.setAttribute("data-theme", mode);
  try { localStorage.setItem("theme", mode); } catch { /* noop */ }
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = mode === "dark" ? "#0c0c0c" : "#faf9f7";
}

function applyAccent(accent: Accent) {
  document.documentElement.setAttribute("data-accent", accent);
  try { localStorage.setItem("accent", accent); } catch { /* noop */ }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<Mode>(getInitialMode);
  const [accent, setAccentState] = useState<Accent>(getInitialAccent);

  useEffect(() => { applyMode(mode); }, [mode]);
  useEffect(() => { applyAccent(accent); }, [accent]);

  const toggle = () => setMode((m) => (m === "light" ? "dark" : "light"));
  const setAccent = (a: Accent) => setAccentState(a);

  return (
    <ThemeContext.Provider value={{ theme: mode, toggle, accent, setAccent }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}

export const ACCENT_OPTIONS: { id: Accent; label: string; hint: string }[] = [
  { id: "indigo", label: "Indigo", hint: "Default — neutral premium" },
  { id: "forest", label: "Forest", hint: "Wellness, dental, clinics" },
  { id: "rose",   label: "Rose",   hint: "Salons, beauty, spa" },
  { id: "amber",  label: "Amber",  hint: "Barber, fitness, classic trades" },
];
